/**
 * Renders the full plot suite once the pipeline reaches `complete`:
 *   - STL anomaly overlay (if STL ran)
 *   - LSTM anomaly overlay (if LSTM ran)
 *   - Processed data plot
 * Plus the "Loading processed data" button (Markus's spec). The Download CSV
 * button now lives in the page action bar (see useAnomalyDownload) so it
 * stays available in `configuring`, not only `complete`/`loaded`.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Lang } from '../types';
import { UseAnomalyPipeline } from '../useAnomalyPipeline';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import AnomalyChart from './AnomalyChart';
import AnomalyOverlayChart from './AnomalyOverlayChart';
import UseProcessedDataButton from './UseProcessedDataButton';

interface Props {
  pipeline: UseAnomalyPipeline;
  lang: Lang;
  onUseProcessedSuccess?: () => void;
}

const AnomalyResultsSection: React.FC<Props> = ({ pipeline, lang, onUseProcessedSuccess }) => {
  const { t } = useTranslation('dataProcessing');
  const { state } = pipeline;

  // Shared brush range drives synchronized zoom/pan across the result charts
  // (STL overlay, LSTM overlay, processed line). Each chart broadcasts its
  // local zoom changes through this state; siblings pick them up via the
  // sharedBrushRange prop.
  const [sharedBrushRange, setSharedBrushRange] = useState<
    { startTime: number; endTime: number } | null
  >(null);

  if (state.status !== 'complete' && state.status !== 'loaded') return null;

  const { plots } = state;

  return (
    <div className="mt-8 space-y-4">
      {plots.stlAnomalies && (
        <AnomalyOverlayChart
          plot={plots.stlAnomalies}
          sharedBrushRange={sharedBrushRange}
          onSharedBrushChange={setSharedBrushRange}
        />
      )}
      {plots.lstmAnomalies && (
        <AnomalyOverlayChart
          plot={plots.lstmAnomalies}
          sharedBrushRange={sharedBrushRange}
          onSharedBrushChange={setSharedBrushRange}
        />
      )}
      {plots.processed && (
        <AnomalyChart
          plot={plots.processed}
          hidePointsToggle
          sharedBrushRange={sharedBrushRange}
          onSharedBrushChange={setSharedBrushRange}
          headerContent={
            <div className="flex items-center gap-1 mb-2">
              <h5 className="text-sm font-medium text-foreground">{t('processedData')}</h5>
              <InfoTooltip text={t('processedDataPointsHint')} />
            </div>
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {state.status === 'complete' && (
          <UseProcessedDataButton pipeline={pipeline} lang={lang} onSuccess={onUseProcessedSuccess} />
        )}
      </div>
    </div>
  );
};

export default AnomalyResultsSection;
